import React, { useMemo, useState } from "react";
import api, { apiError } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { School, Search, Check } from "lucide-react";
import { toast } from "sonner";

// المدير العام يوزّع المدارس على المشرف
export const SupervisorSchoolsDialog = ({ sup, schools, onClose, onSaved }) => {
  const [picked, setPicked] = useState(new Set(sup.supervised_school_ids || []));
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [onlyPicked, setOnlyPicked] = useState(false);
  const [saving, setSaving] = useState(false);

  const shown = useMemo(() => schools.filter((s) =>
    (type === "all" || s.type === type) && (!onlyPicked || picked.has(s.id)) && (!q.trim() || (s.name || "").includes(q.trim()))
  ), [schools, type, onlyPicked, picked, q]);
  const allShown = shown.length > 0 && shown.every((s) => picked.has(s.id));

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setPicked((p) => { const n = new Set(p); shown.forEach((s) => (allShown ? n.delete(s.id) : n.add(s.id))); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/supervisors/${sup.id}/schools`, { school_ids: [...picked] });
      toast.success(`تم ربط ${r.data.count} مدرسة بالمشرف ${sup.name}${r.data.moved_from_others ? " (ونُقلت مدارس كانت مع مشرف آخر)" : ""}`);
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const chip = (k, label) => (
    <button key={k} type="button" data-testid={`sup-type-${k}`} onClick={() => setType(k)}
      className={`px-3 py-1.5 rounded-full text-xs border ${type === k ? "bg-amber-600 text-white border-amber-600" : "bg-white border-amber-400/40 text-stone-700"}`}>{label}</button>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-2xl max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="sup-schools-dialog">
        <DialogHeader><DialogTitle className="gold-text font-head flex items-center gap-2"><School size={18} /> مدارس المشرف — {sup.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1.5 items-center">
          {chip("all", "الكل")}{chip("بنين", "بنين")}{chip("بنات", "بنات")}
          <label className="text-xs flex items-center gap-1 mr-2"><input type="checkbox" data-testid="sup-only-picked" checked={onlyPicked} onChange={(e) => setOnlyPicked(e.target.checked)} /> المحددة فقط</label>
        </div>
        <div className="relative">
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="sup-school-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم المدرسة..."
            className="w-full pr-9 px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-sm focus:outline-none" />
        </div>
        <div className="flex items-center justify-between text-xs">
          <button type="button" data-testid="sup-select-all" onClick={toggleAll} disabled={!shown.length} className="px-3 py-1 rounded-lg border border-emerald-500/40 bg-emerald-50 text-emerald-700 disabled:opacity-50">
            {allShown ? "إلغاء تحديد المعروض" : `تحديد المعروض (${shown.length})`}
          </button>
          <span data-testid="sup-picked-count">المحدد: {picked.size}</span>
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {shown.map((s) => {
            const on = picked.has(s.id);
            return (
              <button key={s.id} type="button" data-testid={`sup-pick-${s.id}`} onClick={() => toggle(s.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm text-right ${on ? "bg-emerald-500/15 border-emerald-400/50" : "bg-white border-amber-400/20 hover:bg-amber-100/50"}`}>
                <span className="truncate">{s.name} <span className="text-[10px] text-stone-400">{s.type}</span></span>
                <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-emerald-600 text-white" : "border border-stone-300"}`}>{on && <Check size={13} />}</span>
              </button>
            );
          })}
          {!shown.length && <div className="text-center text-stone-500 text-sm py-4">لا توجد مدارس مطابقة</div>}
        </div>
        <button data-testid="sup-schools-save" onClick={save} disabled={saving} className="btn-gold px-5 py-2.5 rounded-xl disabled:opacity-60">{saving ? "جارٍ الحفظ..." : "حفظ الربط"}</button>
      </DialogContent>
    </Dialog>
  );
};

export default SupervisorSchoolsDialog;
